import { api } from './client';
import type { Page, Person, PersonInput, PersonKind, UUID, WriteResult } from './types';

export interface PersonsListParams {
  /** Multi-select (C-132 amendment): repeated query keys via qs(). */
  kind?: PersonKind[];
  q?: string;
  include_deleted?: boolean;
  /** #39 (2026-06-11): the Deleted bucket alone (two-bucket pills). */
  only_deleted?: boolean;
  /** B2-8: which alive persons by DERIVED activity — 'active' (holds a role on an
   *  Active or Prospect agreement, or an Active account/policy) / 'inactive' (alive, no such role) / 'all'
   *  (default omitted — pickers, global search, legacy callers see everyone). */
  alive?: 'all' | 'active' | 'inactive';
  // #46 Wave 1 (2026-06-12): per-column predicate filters (C-132).
  // Closed-set filters are multi-select; us_person Yes+No = "answered".
  email_q?: string;
  tin_q?: string;
  pep_status?: string[];
  us_person?: boolean[];
  /** Pedro 2026-10-10: Agreement column — text filter on "CIIF-nnn · MNEMONIC" + sort. */
  agreement_q?: string;
  /** P13: receives (true) / doesn't (false) per communication type; both = all. */
  comms_monthly_report?: boolean[];
  comms_newsletter?: boolean[];
  comms_event_invitations?: boolean[];
  comms_commercial_offers?: boolean[];
  /** P16b G1: natural persons still missing their given names or surnames (name review). */
  missing_name_parts?: boolean;
  /** P41: a column key, '-' prefix to reverse (server-side). */
  sort?: string;
  limit?: number;
  offset?: number;
}

/** P13: the Admin-only CSV of the Persons list AS FILTERED (same params as the list,
 *  without paging/sort). Same-origin GET; the session cookie carries auth. */
export function personsExportHref(params: PersonsListParams): string {
  const { limit: _l, offset: _o, sort: _s, ...filters } = params;
  return `/persons/export.xlsx${qs(filters)}`;
}

function qs(params: object): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    // #46 (2026-06-12): arrays expand to repeated keys (multi-select,
    // C-132 amendment) — matches FastAPI's list parsing.
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item !== undefined && item !== null && item !== '') {
          usp.append(k, String(item));
        }
      }
    } else {
      usp.set(k, String(v));
    }
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

async function writeWithMaker<T>(
  call: () => Promise<{ data: T; status: number; headers: Headers }>,
): Promise<WriteResult<T>> {
  const r = await call();
  return {
    entity: r.data,
    pending: r.headers.get('X-Change-Request-Status') === 'pending',
    changeRequestId: r.headers.get('X-Change-Request-Id'),
  };
}

// Beta #11 cascade extension (2026-05-26). Row-level reference labels
// power the CascadeDeleteModal.
export interface ReferenceRow {
  id: UUID;
  label: string;
}

export interface ReferencesReport {
  entity_type: string;
  entity_id: UUID;
  has_active_references: boolean;
  summary: string;
  references: Record<string, ReferenceRow[]>;
}

export const personsApi = {
  list:    (params: PersonsListParams = {}) => api.get<Page<Person>>(`/persons${qs(params)}`),
  get:     (id: UUID, includeDeleted = false) =>
    api.get<Person>(`/persons/${id}${includeDeleted ? '?include_deleted=true' : ''}`),
  create:  (payload: PersonInput)           => writeWithMaker(() => api.postMeta<Person>('/persons', payload)),
  update:  (id: UUID, payload: PersonInput) => writeWithMaker(() => api.patchMeta<Person>(`/persons/${id}`, payload)),
  delete:  (id: UUID)                       => writeWithMaker(() => api.deleteMeta<Person>(`/persons/${id}`)),
  restore: (id: UUID)                       => writeWithMaker(() => api.postMeta<Person>(`/persons/${id}/restore`)),
  references:    (id: UUID) => api.get<ReferencesReport>(`/persons/${id}/references`),
  cascadeDelete: (id: UUID, refIds: Record<string, UUID[]>) =>
    writeWithMaker(() => api.postMeta<Person>(`/persons/${id}/cascade-delete`, { ref_ids: refIds })),
};
